import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Inbox, FileText, Briefcase, LogOut, Plus, Trash2, Sparkles, X, Eye, EyeOff, Pencil,
} from "lucide-react";
import { toast } from "sonner";
import Seo from "../../components/Seo";
import { LogoMark } from "../../components/Logo";
import { useAuth } from "../../context/AuthContext";
import { api, formatApiError } from "../../api";
import { INSIGHT_CATEGORIES, CASE_FILTERS } from "../../data/content";

const LEAD_STATUSES = ["New", "Contacted", "In Discussion", "Proposal Sent", "Converted", "Closed"];
const STATUS_COLORS = {
  New: "text-blue-300 border-blue-500/40 bg-blue-600/10",
  Contacted: "text-cyan-300 border-cyan-500/40 bg-cyan-600/10",
  "In Discussion": "text-violet-300 border-violet-500/40 bg-violet-600/10",
  "Proposal Sent": "text-amber-300 border-amber-500/40 bg-amber-600/10",
  Converted: "text-emerald-300 border-emerald-500/40 bg-emerald-600/10",
  Closed: "text-slate-400 border-slate-600 bg-slate-700/20",
};

const inputCls = "w-full rounded-lg bg-slate-950/70 border border-slate-700 focus:border-blue-500/60 focus:outline-none px-3.5 py-2.5 text-sm text-slate-200 transition-colors";
const labelCls = "block text-[11px] font-semibold text-slate-400 mb-1.5";

function Overview({ stats }) {
  if (!stats) return null;
  const cards = [
    ["Total Leads", stats.total_leads],
    ["New Enquiries", stats.new_enquiries],
    ["Project Requests", stats.project_requests],
    ["Published Articles", stats.published_articles],
    ["Case Studies", stats.case_studies],
  ];
  return (
    <div data-testid="admin-overview">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {cards.map(([label, value]) => (
          <div key={label} className="rounded-xl border border-slate-800 bg-slate-900/60 p-5" data-testid={`admin-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <p className="font-mono-accent text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
            <p className="font-display text-3xl font-extrabold text-slate-50 mt-2">{value}</p>
          </div>
        ))}
      </div>
      <h3 className="font-display text-lg font-bold text-slate-50 mt-10 mb-4">Recent Enquiries</h3>
      <div className="space-y-3">
        {(stats.recent || []).map((e) => (
          <div key={e.id} className="rounded-xl border border-slate-800 bg-slate-900/60 p-4 flex flex-wrap items-center gap-4 justify-between" data-testid={`recent-enquiry-${e.id.slice(0, 8)}`}>
            <div>
              <p className="text-sm font-semibold text-slate-200">{e.name} <span className="text-slate-500 font-normal">· {e.type}</span></p>
              <p className="text-xs text-slate-500 mt-0.5">{e.email} · {e.phone}</p>
            </div>
            <span className={`text-[11px] font-semibold border rounded-full px-3 py-1 ${STATUS_COLORS[e.status] || STATUS_COLORS.New}`}>{e.status}</span>
          </div>
        ))}
        {(stats.recent || []).length === 0 && <p className="text-sm text-slate-500">No enquiries yet.</p>}
      </div>
    </div>
  );
}

function Enquiries() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [openId, setOpenId] = useState(null);
  const [draft, setDraft] = useState({});

  const load = useCallback(() => {
    const params = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : "";
    api.get(`/admin/enquiries${params}`).then((r) => setItems(r.data.items)).catch((e) => toast.error(formatApiError(e)));
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    await api.patch(`/admin/enquiries/${id}`, { status }).catch((e) => toast.error(formatApiError(e)));
    load();
  };
  const saveNotes = async (id) => {
    await api.patch(`/admin/enquiries/${id}`, draft).then(() => toast.success("Saved")).catch((e) => toast.error(formatApiError(e)));
    setOpenId(null);
    load();
  };
  const archive = async (id) => {
    await api.delete(`/admin/enquiries/${id}`).catch((e) => toast.error(formatApiError(e)));
    load();
  };

  return (
    <div data-testid="admin-enquiries">
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <button onClick={() => setStatusFilter("")} data-testid="enquiry-filter-all" className={`rounded-full px-3.5 py-1.5 text-xs font-semibold ${statusFilter === "" ? "bg-blue-600 text-white" : "border border-slate-700 text-slate-400"}`}>All</button>
        {LEAD_STATUSES.map((s) => (
          <button key={s} onClick={() => setStatusFilter(statusFilter === s ? "" : s)} data-testid={`enquiry-filter-${s.toLowerCase().replace(/\s+/g, "-")}`} className={`rounded-full px-3.5 py-1.5 text-xs font-semibold ${statusFilter === s ? "bg-blue-600 text-white" : "border border-slate-700 text-slate-400"}`}>
            {s}
          </button>
        ))}
      </div>
      <div className="space-y-3">
        {items.map((e) => (
          <div key={e.id} className="rounded-xl border border-slate-800 bg-slate-900/60 p-5" data-testid={`enquiry-row-${e.id.slice(0, 8)}`}>
            <div className="flex flex-wrap items-center gap-3 justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-100">
                  {e.name} {e.company ? <span className="text-slate-500 font-normal">· {e.company}</span> : null}
                </p>
                <p className="text-xs text-slate-500 mt-1">{e.email} · {e.phone} · {new Date(e.created_at).toLocaleString()}</p>
              </div>
              <div className="flex items-center gap-2.5">
                <span className="text-[10px] font-mono-accent uppercase tracking-widest text-slate-500 border border-slate-700 rounded-full px-2.5 py-1">{e.type}</span>
                <select
                  value={e.status}
                  onChange={(ev) => setStatus(e.id, ev.target.value)}
                  data-testid={`enquiry-status-${e.id.slice(0, 8)}`}
                  className="rounded-lg bg-slate-950 border border-slate-700 text-xs text-slate-200 px-2.5 py-1.5 focus:outline-none"
                >
                  {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <button
                  onClick={() => { setOpenId(openId === e.id ? null : e.id); setDraft({ notes: e.notes || "", follow_up_date: e.follow_up_date || "" }); }}
                  data-testid={`enquiry-expand-${e.id.slice(0, 8)}`}
                  className="text-xs text-blue-400 hover:text-blue-300 font-semibold"
                >
                  {openId === e.id ? "Close" : "Details"}
                </button>
                <button onClick={() => archive(e.id)} data-testid={`enquiry-archive-${e.id.slice(0, 8)}`} className="text-slate-500 hover:text-red-400 transition-colors" aria-label="Archive enquiry">
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
            {openId === e.id && (
              <div className="mt-4 pt-4 border-t border-slate-800 space-y-4">
                <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">{e.message}</p>
                {e.services?.length > 0 && <p className="text-xs text-slate-500">Services: {e.services.join(", ")}</p>}
                {e.budget && <p className="text-xs text-slate-500">Budget: {e.budget}</p>}
                <div className="grid sm:grid-cols-[1fr_180px_120px] gap-3 items-end">
                  <div>
                    <label className={labelCls}>Internal Notes</label>
                    <textarea rows={2} value={draft.notes} onChange={(ev) => setDraft({ ...draft, notes: ev.target.value })} data-testid={`enquiry-notes-${e.id.slice(0, 8)}`} className={`${inputCls} resize-none`} />
                  </div>
                  <div>
                    <label className={labelCls}>Follow-up Date</label>
                    <input type="date" value={draft.follow_up_date} onChange={(ev) => setDraft({ ...draft, follow_up_date: ev.target.value })} data-testid={`enquiry-followup-${e.id.slice(0, 8)}`} className={inputCls} />
                  </div>
                  <button onClick={() => saveNotes(e.id)} data-testid={`enquiry-save-${e.id.slice(0, 8)}`} className="rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold px-4 py-2.5 transition-colors">
                    Save
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-slate-500">No enquiries found.</p>}
      </div>
    </div>
  );
}

const EMPTY_ARTICLE = { title: "", slug: "", category: INSIGHT_CATEGORIES[0], tags: [], author: "Trendsight Media", summary: "", content: "", featured_image: "", status: "draft", seo_title: "", seo_description: "" };

function Articles() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [aiBusy, setAiBusy] = useState("");

  const load = useCallback(() => {
    api.get("/admin/articles").then((r) => setItems(r.data.items)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      if (editing.id) await api.put(`/admin/articles/${editing.id}`, editing);
      else await api.post("/admin/articles", editing);
      toast.success("Article saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const runAi = async (task) => {
    if (!editing.title) return toast.error("Enter a title/topic first");
    setAiBusy(task);
    try {
      const { data } = await api.post("/admin/ai/generate", { task, topic: editing.title, category: editing.category });
      if (task === "article_draft") setEditing((s) => ({ ...s, content: data.result }));
      else {
        const title = (data.result.match(/TITLE:\s*(.+)/i) || [])[1]?.trim();
        const desc = (data.result.match(/DESCRIPTION:\s*(.+)/i) || [])[1]?.trim();
        setEditing((s) => ({ ...s, seo_title: title || s.seo_title, seo_description: desc || s.seo_description }));
      }
      toast.success("AI content generated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAiBusy("");
    }
  };

  const togglePublish = async (a) => {
    await api.put(`/admin/articles/${a.id}`, { ...a, status: a.status === "published" ? "draft" : "published" }).catch((e) => toast.error(formatApiError(e)));
    load();
  };
  const remove = async (id) => {
    await api.delete(`/admin/articles/${id}`).catch((e) => toast.error(formatApiError(e)));
    load();
  };

  if (editing) {
    return (
      <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-6 space-y-5" data-testid="article-editor">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-slate-50">{editing.id ? "Edit Article" : "New Article"}</h3>
          <button onClick={() => setEditing(null)} className="text-slate-500 hover:text-slate-300" aria-label="Close editor" data-testid="article-editor-close"><X size={18} /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Title *</label>
            <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="article-title-input" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Slug</label>
            <input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} placeholder="auto-generated" data-testid="article-slug-input" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Category</label>
            <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} data-testid="article-category-select" className={inputCls}>
              {INSIGHT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Tags (comma separated)</label>
            <input value={editing.tags.join(", ")} onChange={(e) => setEditing({ ...editing, tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })} data-testid="article-tags-input" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Author</label>
            <input value={editing.author} onChange={(e) => setEditing({ ...editing, author: e.target.value })} data-testid="article-author-input" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Status</label>
            <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} data-testid="article-status-select" className={inputCls}>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>
        <div>
          <label className={labelCls}>Summary</label>
          <textarea rows={2} value={editing.summary} onChange={(e) => setEditing({ ...editing, summary: e.target.value })} data-testid="article-summary-input" className={`${inputCls} resize-none`} />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className={labelCls}>Content (markdown, ## for headings)</label>
            <div className="flex gap-2">
              <button onClick={() => runAi("article_draft")} disabled={!!aiBusy} data-testid="ai-draft-btn" className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-blue-400 hover:text-blue-300 disabled:opacity-50">
                <Sparkles size={12} /> {aiBusy === "article_draft" ? "Generating..." : "AI Draft"}
              </button>
              <button onClick={() => runAi("seo_meta")} disabled={!!aiBusy} data-testid="ai-seo-btn" className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-cyan-400 hover:text-cyan-300 disabled:opacity-50">
                <Sparkles size={12} /> {aiBusy === "seo_meta" ? "Generating..." : "AI SEO Meta"}
              </button>
            </div>
          </div>
          <textarea rows={10} value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} data-testid="article-content-input" className={`${inputCls} resize-y font-mono text-[13px]`} />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>SEO Title</label>
            <input value={editing.seo_title} onChange={(e) => setEditing({ ...editing, seo_title: e.target.value })} data-testid="article-seo-title-input" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>SEO Description</label>
            <input value={editing.seo_description} onChange={(e) => setEditing({ ...editing, seo_description: e.target.value })} data-testid="article-seo-desc-input" className={inputCls} />
          </div>
        </div>
        <div className="flex gap-3">
          <button onClick={save} data-testid="article-save-btn" className="rounded-full bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold px-7 py-3 transition-colors">Save Article</button>
          <button onClick={() => setEditing(null)} className="rounded-full border border-slate-700 text-slate-400 text-sm font-semibold px-7 py-3">Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-articles">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-slate-400">{items.length} articles</p>
        <button onClick={() => setEditing({ ...EMPTY_ARTICLE })} data-testid="article-new-btn" className="inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold px-5 py-2.5 transition-colors">
          <Plus size={14} /> New Article
        </button>
      </div>
      <div className="space-y-3">
        {items.map((a) => (
          <div key={a.id} className="rounded-xl border border-slate-800 bg-slate-900/60 p-4 flex flex-wrap items-center gap-4 justify-between" data-testid={`article-row-${a.slug}`}>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-100 truncate">{a.title}</p>
              <p className="text-xs text-slate-500 mt-0.5">{a.category} · /insights/{a.slug}</p>
            </div>
            <div className="flex items-center gap-2.5">
              <span className={`text-[10px] font-mono-accent uppercase tracking-widest border rounded-full px-2.5 py-1 ${a.status === "published" ? "text-emerald-300 border-emerald-500/40" : "text-slate-400 border-slate-700"}`}>{a.status}</span>
              <button onClick={() => setEditing(a)} data-testid={`article-edit-${a.slug}`} className="text-slate-400 hover:text-blue-400 transition-colors" aria-label="Edit article"><Pencil size={15} /></button>
              <button onClick={() => togglePublish(a)} data-testid={`article-toggle-${a.slug}`} className="text-slate-400 hover:text-emerald-400 transition-colors" aria-label="Toggle publish">
                {a.status === "published" ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
              <button onClick={() => remove(a.id)} data-testid={`article-delete-${a.slug}`} className="text-slate-500 hover:text-red-400 transition-colors" aria-label="Delete article"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const EMPTY_CS = { title: "", client: "", industry: "", services: [], categories: [], challenge: "", objective: "", strategy: "", execution: "", highlights: [], results: [], testimonial_quote: "", testimonial_author: "", featured_image: "", conclusion: "", status: "published", is_demo: true, slug: "" };

function CaseStudies() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/case-studies").then((r) => setItems(r.data.items)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      if (editing.id) await api.put(`/admin/case-studies/${editing.id}`, editing);
      else await api.post("/admin/case-studies", editing);
      toast.success("Case study saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const remove = async (id) => {
    await api.delete(`/admin/case-studies/${id}`).catch((e) => toast.error(formatApiError(e)));
    load();
  };

  if (editing) {
    const setList = (key, sep) => (e) => setEditing({ ...editing, [key]: e.target.value.split(sep).map((s) => s.trim()).filter(Boolean) });
    return (
      <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-6 space-y-5" data-testid="case-editor">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-slate-50">{editing.id ? "Edit Case Study" : "New Case Study"}</h3>
          <button onClick={() => setEditing(null)} className="text-slate-500 hover:text-slate-300" aria-label="Close editor" data-testid="case-editor-close"><X size={18} /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Project Title *</label>
            <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="case-title-input" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Client</label>
            <input value={editing.client} onChange={(e) => setEditing({ ...editing, client: e.target.value })} data-testid="case-client-input" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Industry</label>
            <input value={editing.industry} onChange={(e) => setEditing({ ...editing, industry: e.target.value })} data-testid="case-industry-input" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Services Used (comma separated)</label>
            <input value={editing.services.join(", ")} onChange={setList("services", ",")} data-testid="case-services-input" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Filter Categories</label>
            <div className="flex flex-wrap gap-2 pt-1" data-testid="case-categories-options">
              {CASE_FILTERS.filter((f) => f !== "All").map((c) => (
                <button
                  type="button"
                  key={c}
                  onClick={() => setEditing({ ...editing, categories: editing.categories.includes(c) ? editing.categories.filter((x) => x !== c) : [...editing.categories, c] })}
                  data-testid={`case-category-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`rounded-full px-3 py-1.5 text-[11px] font-semibold ${editing.categories.includes(c) ? "bg-blue-600 text-white" : "border border-slate-700 text-slate-400"}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-end gap-4 pb-1">
            <label className="flex items-center gap-2 text-xs text-slate-300">
              <input type="checkbox" checked={editing.is_demo} onChange={(e) => setEditing({ ...editing, is_demo: e.target.checked })} data-testid="case-demo-checkbox" />
              Marked as illustrative demo
            </label>
          </div>
        </div>
        {[["challenge", "Challenge"], ["objective", "Objective"], ["strategy", "Strategy"], ["execution", "Execution"], ["conclusion", "Conclusion"]].map(([key, label]) => (
          <div key={key}>
            <label className={labelCls}>{label}</label>
            <textarea rows={3} value={editing[key]} onChange={(e) => setEditing({ ...editing, [key]: e.target.value })} data-testid={`case-${key}-input`} className={`${inputCls} resize-none`} />
          </div>
        ))}
        <div>
          <label className={labelCls}>Highlights (one per line)</label>
          <textarea rows={3} value={editing.highlights.join("\n")} onChange={setList("highlights", "\n")} data-testid="case-highlights-input" className={`${inputCls} resize-none`} />
        </div>
        <div>
          <label className={labelCls}>Results / KPIs (one per line, "Label | Value")</label>
          <textarea
            rows={4}
            value={editing.results.map((r) => `${r.label} | ${r.value}`).join("\n")}
            onChange={(e) =>
              setEditing({
                ...editing,
                results: e.target.value.split("\n").map((line) => {
                  const [label, value] = line.split("|").map((s) => (s || "").trim());
                  return label && value ? { label, value } : null;
                }).filter(Boolean),
              })
            }
            data-testid="case-results-input"
            className={`${inputCls} resize-none`}
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Testimonial Quote</label>
            <textarea rows={2} value={editing.testimonial_quote} onChange={(e) => setEditing({ ...editing, testimonial_quote: e.target.value })} data-testid="case-testimonial-input" className={`${inputCls} resize-none`} />
          </div>
          <div>
            <label className={labelCls}>Testimonial Author</label>
            <input value={editing.testimonial_author} onChange={(e) => setEditing({ ...editing, testimonial_author: e.target.value })} data-testid="case-testimonial-author-input" className={inputCls} />
          </div>
        </div>
        <div className="flex gap-3">
          <button onClick={save} data-testid="case-save-btn" className="rounded-full bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold px-7 py-3 transition-colors">Save Case Study</button>
          <button onClick={() => setEditing(null)} className="rounded-full border border-slate-700 text-slate-400 text-sm font-semibold px-7 py-3">Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-case-studies">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-slate-400">{items.length} case studies</p>
        <button onClick={() => setEditing({ ...EMPTY_CS })} data-testid="case-new-btn" className="inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold px-5 py-2.5 transition-colors">
          <Plus size={14} /> New Case Study
        </button>
      </div>
      <div className="space-y-3">
        {items.map((cs) => (
          <div key={cs.id} className="rounded-xl border border-slate-800 bg-slate-900/60 p-4 flex flex-wrap items-center gap-4 justify-between" data-testid={`case-row-${cs.slug}`}>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-100 truncate">{cs.title}</p>
              <p className="text-xs text-slate-500 mt-0.5">{cs.industry} · /work/{cs.slug}</p>
            </div>
            <div className="flex items-center gap-2.5">
              {cs.is_demo && <span className="text-[10px] font-mono-accent uppercase tracking-widest text-amber-300 border border-amber-500/40 rounded-full px-2.5 py-1">Demo</span>}
              <button onClick={() => setEditing(cs)} data-testid={`case-edit-${cs.slug}`} className="text-slate-400 hover:text-blue-400 transition-colors" aria-label="Edit case study"><Pencil size={15} /></button>
              <button onClick={() => remove(cs.id)} data-testid={`case-delete-${cs.slug}`} className="text-slate-500 hover:text-red-400 transition-colors" aria-label="Delete case study"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const TABS = [
  ["overview", "Overview", LayoutDashboard],
  ["enquiries", "Enquiries", Inbox],
  ["articles", "Insights", FileText],
  ["cases", "Case Studies", Briefcase],
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [tab]);

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <main className="min-h-screen bg-[#070B14] bg-grid" data-testid="admin-dashboard">
      <Seo title="Admin Dashboard" description="Trendsight Media administration." />
      <header className="glass-panel sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <LogoMark size={30} />
            <div>
              <p className="font-display text-sm font-extrabold text-slate-50 tracking-widest">TRENDSIGHT ADMIN</p>
              <p className="text-[10px] text-slate-500">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="text-xs text-slate-400 hover:text-blue-300 transition-colors" data-testid="admin-view-site-link">View Site</a>
            <button onClick={doLogout} data-testid="admin-logout-btn" className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-red-400 transition-colors">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
        <div className="flex gap-2 mb-8 overflow-x-auto pb-1" data-testid="admin-tabs">
          {TABS.map(([key, label, IconCmp]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`admin-tab-${key}`}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold whitespace-nowrap transition-all duration-300 ${
                tab === key ? "bg-blue-600 text-white shadow-lg shadow-blue-600/25" : "border border-slate-700 text-slate-400 hover:text-blue-300 hover:border-blue-500/50"
              }`}
            >
              <IconCmp size={15} /> {label}
            </button>
          ))}
        </div>
        {tab === "overview" && <Overview stats={stats} />}
        {tab === "enquiries" && <Enquiries />}
        {tab === "articles" && <Articles />}
        {tab === "cases" && <CaseStudies />}
      </div>
    </main>
  );
}
